import { fgColumnEntries } from "@redotech/redo-fulfillment-common/views/fulfillment/fg-column-catalog";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { ColumnAlignment } from "@redotech/redo-web/table";
import { Column2 } from "@redotech/redo-web/table2";
import {
  formatDateString,
  getExpandedItems,
} from "../../utils/outbound-labels-utils";

const catalogEntry = fgColumnEntries.productBinAssignmentDate;

type LineItemPickability = NonNullable<
  FulfillmentGroupData["lineItemPickability"]
>[number];

// Earliest inventory_level created_at across this item's bin/storage locations —
// when the product was first assigned to a warehouse bin (receiving excluded).
// Precomputed server-side so the column and the matching filter stay in lockstep.
// Undefined when the item has no bin/storage assignment.
function earliestAssignmentForLineItem(
  lineItem: LineItemPickability,
): Date | undefined {
  return lineItem.earliestBinAssignmentAt ?? undefined;
}

export const ProductBinAssignmentDateColumn: Column2<FulfillmentGroupData> = {
  alignment: ColumnAlignment.LEFT,
  key: catalogEntry.key,
  title: catalogEntry.title,
  renderNormalCell: ({ row }) => {
    const lineItems = row.lineItemPickability;
    if (!lineItems || lineItems.length === 0) {
      return <RedoTableTextCell text="" />;
    }
    const perItemEarliest = lineItems.map(earliestAssignmentForLineItem);
    // Show nothing unless every line item has at least one bin/storage assignment.
    if (perItemEarliest.includes(undefined)) {
      return <RedoTableTextCell text="" />;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const latest = (perItemEarliest as Date[]).reduce((max, date) =>
      date.getTime() > max.getTime() ? date : max,
    );
    return (
      <RedoTableTextCell text={formatDateString(latest.toISOString(), false)} />
    );
  },
  renderExpandedCell: ({ row, expansionIndex }) => {
    const lineItems = row.lineItemPickability;
    if (!lineItems || lineItems.length === 0) {
      return null;
    }
    const expandedItem = getExpandedItems(row)[expansionIndex];
    if (!expandedItem) {
      return null;
    }
    const item = lineItems.find(
      (li) => li.externalId === expandedItem.externalId,
    );
    if (!item) {
      return null;
    }
    const earliest = earliestAssignmentForLineItem(item);
    return (
      <RedoTableTextCell
        text={earliest ? formatDateString(earliest.toISOString(), false) : ""}
      />
    );
  },
  width: catalogEntry.defaultWidth,
  sort: catalogEntry.sortDirection,
};
